import React, { useState, useEffect, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Stack,
    Button,
    Chip
} from '@mui/material';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import Business from '@mui/icons-material/Business';
import { BasicPage } from '../components/BasicPage';
import { PunchoutCart } from '../components/PunchoutCart';
import { useAuth } from '../hooks/useAuth';
import { Context } from '../context/appContext';

/**
 * PunchoutPage Component
 * 
 * Main page for punchout functionality
 * Handles customer punchout sessions and displays the shopping cart
 */
export const PunchoutPage = () => {
    console.log('🚀 PunchoutPage - Component rendering');

    const { user, claims, accountID } = useAuth();
    const { dispatch } = useContext(Context);
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [punchoutType, setPunchoutType] = useState('cxml');
    const [returnUrl, setReturnUrl] = useState(null);
    const [buyerCookie, setBuyerCookie] = useState(null);

    console.log('🔍 PunchoutPage - Current state:', {
        loading,
        error,
        sessionId,
        punchoutType,
        returnUrl,
        buyerCookie,
        hasUser: !!user,
        hasClaims: !!claims,
        searchParams: searchParams.toString()
    });

    useEffect(() => {
        console.log('⚡ PunchoutPage - useEffect triggered');
        console.log('📍 URL:', window.location.href);
        console.log('📋 Search Params:', searchParams.toString());
        
        // Always initialize, even if user not ready yet
        initializePunchout();
    }, [searchParams]);

    const initializePunchout = async () => {
        try {
            setLoading(true);
            setError(null);
            sessionStorage.removeItem('punchoutTransferInProgress');

            // Extract punchout parameters from URL
            const sessionIdParam = searchParams.get('sessionId');
            const returnUrlParam = searchParams.get('returnUrl');
            const punchoutTypeParam = searchParams.get('punchoutType') || 'cxml';
            const buyerCookieParam = searchParams.get('buyerCookie');
            const tokenParam = searchParams.get('token');

            // Remove token from URL if still present (security measure)
            if (tokenParam) {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('token');
                const cleanUrl = `/web/punchout?${newParams.toString()}`;
                window.history.replaceState({}, '', cleanUrl);
                console.log('🔒 Token removed from URL for security');
            }

            // Check if we have stored punchout parameters (fallback for login scenarios)
            if (!sessionIdParam) {
                const storedParams = sessionStorage.getItem('punchoutParams');
                if (storedParams) {
                    console.log('🔄 Found stored punchout parameters, redirecting with them:', storedParams);
                    window.location.href = `/web/punchout${storedParams}`;
                    return;
                }
            }

            console.log('✅ PunchoutPage - Extracted URL params:', {
                sessionIdParam,
                returnUrlParam,
                punchoutTypeParam,
                buyerCookieParam
            });

            if (!sessionIdParam) {
                throw new Error('Punchout session ID is required. Add ?sessionId=TEST_SESSION to the URL.');
            }

            setSessionId(sessionIdParam);
            setPunchoutType(punchoutTypeParam);
            setReturnUrl(returnUrlParam);
            setBuyerCookie(buyerCookieParam || sessionIdParam);

            // Set punchout session in global context for persistent tracking
            if (sessionIdParam && punchoutTypeParam) {
                dispatch({
                    type: 'SET_PUNCHOUT_SESSION',
                    payload: {
                        sessionId: sessionIdParam,
                        punchoutType: punchoutTypeParam,
                        returnUrl: returnUrlParam,
                        buyerCookie: buyerCookieParam || sessionIdParam
                    }
                });
                console.log('✅ Punchout session set in global context');
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCartTransfer = (result) => {
        console.log('Cart transferred successfully:', result);
        // Handle successful cart transfer
    };

    if (loading) {
        return (
            <BasicPage title="Punchout" icon={<ShoppingCart />}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        Initializing punchout session...
                    </Typography>
                </Box>
            </BasicPage>
        );
    }

    if (error) {
        return (
            <BasicPage title="Punchout Error" icon={<ShoppingCart />}>
                <Alert severity="error">
                    {error}
                </Alert>
            </BasicPage>
        );
    }

    return (
        <BasicPage title="Punchout Shopping" icon={<ShoppingCart />}>
            <Stack spacing={3}>
                {/* Punchout Session Info */}
                <Card>
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                            <Business />
                            <Typography variant="h6">
                                Punchout Session
                            </Typography>
                        </Stack>
                        
                        <Stack spacing={1}>
                            <Typography variant="body2">
                                <strong>Type:</strong> {punchoutType.toUpperCase()}
                            </Typography>
                            {returnUrl && (
                                <Typography variant="body2">
                                    <strong>Return URL:</strong> {returnUrl}
                                </Typography>
                            )}
                            {buyerCookie && (
                                <Typography variant="body2">
                                    <strong>Session ID:</strong> {buyerCookie}
                                </Typography>
                            )}
                            {accountID && (
                                <Typography variant="body2">
                                    <strong>Account:</strong> {accountID}
                                </Typography>
                            )}
                        </Stack>
                    </CardContent>
                </Card>

                {/* Punchout Cart */}
                <PunchoutCart
                    punchoutType={punchoutType}
                    returnUrl={returnUrl}
                    buyerCookie={buyerCookie}
                    onCartTransfer={handleCartTransfer}
                />

                {/* Instructions */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            So verwenden Sie Punchout
                        </Typography>
                        <Stack spacing={1}>
                            <Typography variant="body2">
                                1. Durchsuchen Sie unseren Katalog und fügen Sie Artikel zu Ihrem Warenkorb hinzu{' '}
                                <Button
                                    component={Link}
                                    to="/web/searchmeta"
                                    size="small"
                                    variant="text"
                                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none', verticalAlign: 'baseline' }}
                                >
                                    (Zum Shop)
                                </Button>
                            </Typography>
                            <Typography variant="body2">
                                2. Überprüfen Sie Ihre Auswahl im Punchout-Warenkorb oben
                            </Typography>
                            <Typography variant="body2">
                                3. Klicken Sie auf "An Beschaffungssystem übertragen", um Ihren Warenkorb zurück an das Beschaffungssystem Ihres Unternehmens zu senden
                            </Typography>
                            <Typography variant="body2">
                                4. Schließen Sie Ihre Bestellung in Ihrem Beschaffungssystem ab
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </BasicPage>
    );
};
